import type { AuthResponse, JwtPayload, User } from './types';
import { jsonRequestInit } from './utils/jsonRequest';
import { clearSelectedChannel } from './utils/selectedChannel';

let sessionExpiredHandler: (() => void) | null = null;

export function onSessionExpired(handler: () => void): void {
  sessionExpiredHandler = handler;
}

export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - bufferSeconds <= nowSeconds;
}

export function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

export function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Mirrors backend/app/auth.py PASSWORD_MIN_LENGTH; pinned by
// backend/tests/test_auth_policy_frontend_parity.py.
export const PASSWORD_MIN_LENGTH = 8;

// These predicates mirror backend/app/auth.py and read flags only. They used to
// carry copies of the backend's hardcoded super-admin / API-manager *username*
// sets; that grant is gone (it handed privilege to whoever signed up with the
// name first), so there is nothing left to mirror but the booleans the server
// stamps into the JWT and the user row. These gate UI affordances only — every
// privileged action is re-checked server-side.

export function isAdmin(user: User | null): boolean {
  if (!user) return false;
  return !!user.is_admin || isSuperAdmin(user);
}

export function isSuperAdmin(user: User | null): boolean {
  if (!user) return false;
  return !!user.is_super_admin;
}

export function hasApiAccess(user: User | null): boolean {
  if (!user) return false;
  return !!user.api_access_enabled || canManageApiAccess(user);
}

export function canManageApiAccess(user: User | null): boolean {
  if (!user) return false;
  return !!user.is_api_access_manager;
}

/** A user as some server endpoint shaped it: privilege flags may be absent. */
export type ServerUser = Omit<User, 'is_admin'> & { is_admin?: boolean };

/**
 * Fill privilege flags the server user shape omitted from the JWT's claims.
 *
 * The persisted user must always carry is_admin / is_super_admin /
 * api_access_enabled, but not every server user shape includes them all —
 * the login response carries none, and `/api/me` omits is_super_admin — so
 * persisting a raw response can silently under-report privileges. Fields the
 * response *does* carry win (the server row is fresher than a token minted
 * earlier); only missing ones fall back to the token claims. Pure, so it is
 * testable without a browser.
 */
export function withJwtFlags(user: ServerUser, payload: JwtPayload | null): User {
  return {
    ...user,
    is_admin: user.is_admin ?? !!(payload && payload.is_admin),
    is_super_admin: user.is_super_admin ?? !!(payload && payload.is_super_admin),
    api_access_enabled: user.api_access_enabled ?? !!(payload && payload.api_access_enabled),
    is_api_access_manager: user.is_api_access_manager ?? !!(payload && payload.is_api_access_manager),
  };
}

/**
 * The single writer of the persisted `user` slot.
 *
 * Every write routes through here so the stored shape has one owner: the
 * caller's server user is enriched with the JWT-derived privilege flags
 * before it lands in localStorage. Writing the slot directly bypasses that
 * enrichment and strips flags from the stored user (which is exactly what
 * UserProvider's refresh once did to is_super_admin).
 */
export function setStoredUser(user: ServerUser): User {
  const token = getAccessToken();
  const enriched = withJwtFlags(user, token ? decodeJwtPayload(token) : null);
  localStorage.setItem('user', JSON.stringify(enriched));
  return enriched;
}

export function storeAuth(data: AuthResponse): User {
  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  return setStoredUser(data.user);
}

export function clearAuth(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  clearSelectedChannel();
}

export function logout(): void {
  clearAuth();
  sessionExpiredHandler?.();
}

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  // Raw fetch, not apiJson: api.ts calls this to mint the token apiJson
  // itself attaches, so routing through it would recurse.
  const response = await fetch(
    '/api/auth/refresh',
    jsonRequestInit('POST', { refresh_token: refreshToken }),
  );

  if (!response.ok) return null;

  const data = await response.json();
  if (!data.access_token) return null;

  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  const payload = decodeJwtPayload(data.access_token);
  const existingUser = getStoredUser() || {};
  const nextUser: User = {
    username: '',
    email: '',
    ...existingUser,
    is_admin: !!(payload && payload.is_admin),
    is_super_admin: !!(payload && payload.is_super_admin),
    api_access_enabled: !!(payload && payload.api_access_enabled),
    is_api_access_manager: !!(payload && payload.is_api_access_manager),
  };
  localStorage.setItem('user', JSON.stringify(nextUser));
  return data.access_token;
}
